import {
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, matchPath } from "react-router";
import {
  ChevronIcon,
  MenuLineHorizontalIcon,
  OutIcon,
  RemoveIcon,
  Setting_1Icon,
} from "vega-icons";

import { useStore } from "@nanostores/react";
import { tenantConfig } from "@tenant-config";
import { useAuth } from "@vega-api/custom/api/hooks/useAuth";
import { useAuthCompanyInfo } from "@vega-api/custom/api/hooks/useAuthCompanyInfo";
import { useSession } from "@vega-api/custom/SessionContext/SessionContext";
import { type FrontendPortal } from "@vega-api/generated/types/frontendPortal";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import { LayoutContext } from "@vega-core/components/Layout/LayoutContext";
import { useSidebarNavigation } from "@vega-core/components/Navigation/Sidebar/hooks/useSidebarNavigation";
import { NavigationButton } from "@vega-core/components/Navigation/Sidebar/NavigationButton";
import { SidebarLogo } from "@vega-core/components/Navigation/Sidebar/SidebarLogo";
import { type NavigationItem } from "@vega-core/models/navigation";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { CommonPaths, VegaModulesPaths } from "../../paths";

import {
  $mobileNavigation,
  mobileNavigationActions,
} from "./MobileNavigation.store";

type Props = {
  orgNode?: ReactNode;
  navigationItems?: NavigationItem[];
  logoutSource: FrontendPortal;
};

const BANNED_ROUTES: readonly CommonPaths[] = [
  CommonPaths.Welcome,
  CommonPaths.SelectOrg,
];

const SettingsMenu = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  if (!isOpen) return null;
  const SETTINGS_ITEMS = [
    {
      name: m.settings_titles_profile(),
      href: VegaModulesPaths.SettingsPersonalDetails,
    },
    {
      name: m.settings_titles_display(),
      href: VegaModulesPaths.SettingsDisplay,
    },
    {
      name: m.titles_notifications(),
      href: VegaModulesPaths.SettingsNotifications,
    },
    {
      name: m.settings_titles_support(),
      href: VegaModulesPaths.SettingsSupport,
    },
  ] as const;

  return (
    <nav className="space-y-1" id="settings-menu">
      {SETTINGS_ITEMS.map((item) => (
        <Link
          className={cnTwMerge(
            "flex items-center pl-11 py-2 body-base-medium rounded-companyBorderRadius hover:bg-background/component transition-colors defaultTransition text-text/sidebar/secondary hover:text-text/sidebar/primary",
          )}
          key={item.name}
          onClick={onClose}
          to={item.href}
        >
          {item.name}
        </Link>
      ))}
    </nav>
  );
};

export const MobileNavigation = ({ orgNode, logoutSource }: Props) => {
  const homePath = "/";
  const { logout } = useAuth();
  const [isLogoutLoading, setIsLogoutLoading] = useState(false);
  const { user } = useSession();
  const { navigationItems } = useContext(LayoutContext);
  const { logoDark, logoLight, name: companyName } = useAuthCompanyInfo();
  const { sidebarLogo, smallSidebarLogo } =
    tenantConfig.branding.logos.sidebar ?? {};
  const { getActivePage } = useSidebarNavigation(navigationItems, homePath);

  const { isOrgPopoverOpen, isNavOpen, isSettingsOpen } =
    useStore($mobileNavigation);

  const isBannedRoutes = BANNED_ROUTES.some((route) =>
    matchPath(route, window.location.pathname),
  );

  useEffect(() => {
    if (isNavOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.removeProperty("overflow");
    }

    return () => {
      document.body.style.removeProperty("overflow");
    };
  }, [isNavOpen]);

  const filteredNavigationItems = useMemo(
    () => navigationItems.filter((item) => item.id !== "settings"),
    [navigationItems],
  );

  if (!user || isBannedRoutes) {
    return null;
  }

  const handleLogout = async () => {
    setIsLogoutLoading(true);

    try {
      await logout({
        isGlobal: true,
        savePath: false,
        source: logoutSource,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLogoutLoading(false);
    }
  };

  return (
    <div
      className={cnTwMerge(
        "sticky top-0 w-dvw flex flex-col z-navbar md:hidden defaultTransition",
        isNavOpen ? "fixed top-0 left-0 h-dvh" : "h-auto max-h-min block",
      )}
    >
      <div
        className={cnTwMerge(
          "flex border-r-2 border-sidebar/border h-full w-full flex-col bg-background/sidebar/secondary px-0 transition-all duration-300",
        )}
      >
        <div
          className={cnTwMerge(
            "flex flex-col flex-1 overflow-y-auto",
            isNavOpen ? "pb-4" : "pb-0",
          )}
        >
          <div
            className={cnTwMerge(
              "flex h-navbar items-center border-b-2 border-b-sidebar/border w-full p-4",
            )}
          >
            <SidebarLogo
              companyName={companyName}
              homePath={homePath}
              isCollapsed={false}
              isMobile
              logoDark={sidebarLogo ?? logoDark}
              logoLight={sidebarLogo ?? logoLight}
              smallLogoDark={smallSidebarLogo ?? logoDark}
              smallLogoLight={smallSidebarLogo ?? logoLight}
            />
            <ThemedIconButton
              ariaLabel="Menu"
              className="ml-auto"
              dataTestId="mobile-navigation"
              icon={
                isNavOpen ? (
                  <RemoveIcon
                    className={cnTwMerge({
                      "rotate-90": isNavOpen,
                    })}
                  />
                ) : (
                  <MenuLineHorizontalIcon
                    className={cnTwMerge({
                      "rotate-90": isNavOpen,
                    })}
                  />
                )
              }
              onClick={() => mobileNavigationActions.toggleNav()}
            />
          </div>

          {isNavOpen ? (
            <nav
              aria-label="Sidebar"
              className="flex-1 space-y-1 bg-background/sidebar/secondary font-semibold defaultTransition px-3"
            >
              <div className="pt-5">{orgNode}</div>
              <div className="heading-6 text-text/sidebar/secondary px-4 py-2 mt-4">
                {m.navTitles_dashboard()}
              </div>
              {filteredNavigationItems.map((item) => (
                <NavigationButton
                  isActive={getActivePage(item.href, item)}
                  isCollapsed={false}
                  isMobile
                  item={item}
                  key={item.name}
                  onClick={() => mobileNavigationActions.closeNav()}
                  rightIcon={item.rightIcon?.({ isCollapsed: false })}
                />
              ))}
              <div className="space-y-1">
                <NavigationButton
                  isActive={false}
                  isCollapsed={false}
                  isMobile
                  item={{
                    href: VegaModulesPaths.SettingsPersonalDetails,
                    icon: Setting_1Icon,
                    name: m.navTitles_settings(),
                  }}
                  onClick={() =>
                    !isOrgPopoverOpen &&
                    mobileNavigationActions.toggleSettings()
                  }
                  rightIcon={
                    <ChevronIcon
                      aria-hidden="true"
                      className={cnTwMerge(
                        "h-5 w-5 ml-auto transition-transform defaultTransition",
                        isSettingsOpen ? "rotate-180" : "",
                      )}
                    />
                  }
                />
                <SettingsMenu
                  isOpen={isSettingsOpen}
                  onClose={() => mobileNavigationActions.closeAll()}
                />
              </div>
            </nav>
          ) : null}
        </div>
        {isNavOpen ? (
          <div className="w-full h-13 p-3 mb-3">
            <ThemedButton
              data-analytics="gtm-auth-logout"
              dataTestId="mobile-navigation-logout"
              fullWidth
              icon={<OutIcon />}
              isLoading={isLogoutLoading}
              onClick={handleLogout}
              variant="destructive"
            >
              {m.titles_signOut()}
            </ThemedButton>
          </div>
        ) : null}
      </div>
    </div>
  );
};
